import { defineStore } from "pinia";
import { type Ref, ref, computed } from "vue";
import apiSkobo from "@/helpers/api/apiSkobo";
import axios from "axios";

interface MeProfile {
  userId?: string;
  extSysApiUserId?: string;
  emailSchoolAddress?: string;
  roles?: string[];
  permission?: {
    sectionsEdit?: boolean;
  };
  [key: string]: unknown;
}

export const useMeStore = defineStore("me", () => {
  const meProfile: Ref<MeProfile> = ref({});
  const profileForModule: Ref<Record<string, unknown>> = ref({});
  const isCustomerAdminTemporary: Ref<boolean | undefined> = ref(undefined);
  const sessionToken: Ref<string | null> = ref(null);

  const profile = computed(() => meProfile.value);
  const userId = computed(() => meProfile.value?.userId || null);
  const extSysApiUserId = computed(() => meProfile.value?.extSysApiUserId || null);
  const emailSchoolAddress = computed(() => meProfile.value?.emailSchoolAddress || "");

  const isTeacher = computed(() => meProfile.value?.roles?.includes("Teacher") || false);
  const isStudent = computed(() => meProfile.value?.roles?.includes("Student") || false);
  const isSuperuser = computed(() => meProfile.value?.roles?.includes("SuperUser") || false);

  const isConfidentialTeacher = computed(() => meProfile.value?.roles?.includes("ConfidentialTeacher") || false);
  const isConfidentialLeader = computed(() => meProfile.value?.roles?.includes("ConfidentialLeader") || false);
  const hasRole = computed(() => (role: string) => meProfile.value?.roles?.includes(role) || false);

  const highestRole = computed(() => {
    if (meProfile.value?.roles?.includes("SuperUser")) {
      return "SuperUser";
    }
    if (meProfile.value?.roles?.includes("Teacher")) {
      return "Teacher";
    }
    if (meProfile.value?.roles?.includes("Student")) {
      return "Student";
    }
    return "";
  });

  const permissionHasEditSections = computed(() => meProfile.value?.permission?.sectionsEdit === true || false);

  const loadMe = async (me: MeProfile) => {
    meProfile.value = me;
  };

  const loadMyProfileForModule = async (moduleName: string) => {
    if (!profileForModule.value[moduleName]) {
      const response = await apiSkobo.get(`/me/profile/${moduleName}`);
      profileForModule.value[moduleName] = response.data.value;
    }
    return profileForModule.value[moduleName];
  };

  const loadTemporaryCustomerAdmin = async () => {
    if (isCustomerAdminTemporary.value === undefined) {
      const result = (await axios
        .get(
          `https://skobostats.azurewebsites.net/api/CustomerAdminUsers?code=yM9fHa3JpYaEdBvlweQhgQXcy1/JJoS2fWgibebr9afujxzuufHk4w==&pi=${userId.value}`
        )
        .catch(() => {
          return false;
        })) as { status?: number } | false;

      if (result && result !== false && result.status === 200) {
        isCustomerAdminTemporary.value = true;
      } else {
        isCustomerAdminTemporary.value = false;
      }
    }
    return isCustomerAdminTemporary.value;
  };

  const logEvent = (endpoint: string) => {
    apiSkobo.post("/log", {
      endpoint
    });
  };

  return {
    meProfile,
    profileForModule,
    isCustomerAdminTemporary,
    sessionToken,
    profile,
    userId,
    extSysApiUserId,
    emailSchoolAddress,
    isTeacher,
    isStudent,
    isSuperuser,
    isConfidentialTeacher,
    isConfidentialLeader,
    hasRole,
    highestRole,
    permissionHasEditSections,
    loadMe,
    loadMyProfileForModule,
    loadTemporaryCustomerAdmin,
    logEvent
  };
});
